import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, PenLine, Trash2 } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { compressImage } from "../../lib/image";
import { PhotoAnnotator, PhotoOverlay } from "../photos/PhotoAnnotator";
import { Field, NSelect, StepTitle, ActionButton, inputCls } from "../common/Field";

function PhotoCard({ photo, index, anomalies, onChange, onPersist, onDelete, onAnnotate }) {
  const input = (k, placeholder) => (
    <input data-testid={`photo-${index}-${k}`} value={photo[k] || ""} placeholder={placeholder} onChange={(e) => onChange({ [k]: e.target.value })} onBlur={() => onPersist({ [k]: photo[k] || "" })} className={inputCls} />
  );
  return (
    <div className="bg-[#0B1520] border border-[#1E2F45] rounded-sm overflow-hidden fade-up" data-testid={`photo-card-${index}`}>
      <div className="relative bg-black" style={{ aspectRatio: `${photo.width} / ${photo.height}` }}>
        <img src={photo.image} alt={photo.descricao || `Foto ${index + 1}`} className="absolute inset-0 w-full h-full object-contain" />
        <PhotoOverlay photo={photo} />
        <span className="absolute top-2 left-2 bg-[#050B10]/85 border border-[#1E2F45] px-2 py-0.5 font-mono text-[10px] text-[#FFD500]">FOTO {String(index + 1).padStart(2, "0")}</span>
      </div>
      <div className="p-3 flex flex-col gap-2">
        <Field label="Local da fotografia">{input("local", "Ex.: Longarina esquerda, entre eixos")}</Field>
        <Field label="Descrição">{input("descricao")}</Field>
        <Field label="Anomalia relacionada">
          <NSelect testId={`photo-${index}-anomalia`} value={photo.anomalia} onChange={(v) => { onChange({ anomalia: v }); onPersist({ anomalia: v }); }} options={anomalies.map((a) => a.numero)} placeholder="Nenhuma" />
        </Field>
        <Field label="Observação">{input("observacao")}</Field>
        <div className="flex gap-2 mt-1">
          <ActionButton variant="outline" icon={PenLine} onClick={onAnnotate} testId={`photo-${index}-annotate`} className="flex-1">Anotar</ActionButton>
          <button type="button" data-testid={`photo-${index}-delete`} onClick={onDelete} className="h-10 w-10 flex items-center justify-center border border-[#1E2F45] rounded-sm text-slate-400 hover:text-white"><Trash2 size={15} /></button>
        </div>
      </div>
    </div>
  );
}

export default function Step6Photos({ insp, photos, setPhotos }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [annotating, setAnnotating] = useState(null);

  const onFiles = async (files) => {
    setBusy(true);
    let count = photos.length;
    for (const f of Array.from(files)) {
      if (count >= 30) { toast.error("Limite de 30 fotos atingido"); break; }
      try {
        const { dataUrl, width, height } = await compressImage(f);
        const { data } = await api.post(`/inspections/${insp.id}/photos`, { image: dataUrl, width, height });
        setPhotos((ps) => [...ps, data]);
        count += 1;
      } catch (e) {
        toast.error(apiError(e));
      }
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const patchLocal = (id, patch) => setPhotos((ps) => ps.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const persist = async (id, patch) => {
    try { await api.put(`/photos/${id}`, patch); } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Excluir esta foto?")) return;
    try { await api.delete(`/photos/${id}`); setPhotos((ps) => ps.filter((p) => p.id !== id)); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="flex flex-col gap-6">
      <StepTitle n={6} title="Registro fotográfico" subtitle={`Até 30 fotos com local, descrição, anomalia relacionada e anotações (linhas, setas vermelhas e cotas). ${photos.length}/30 fotos.`}
        actions={<ActionButton icon={ImagePlus} onClick={() => fileRef.current?.click()} disabled={busy || photos.length >= 30} testId="btn-adicionar-foto">{busy ? "Enviando..." : "Adicionar foto"}</ActionButton>} />
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="photo-file-input" onChange={(e) => onFiles(e.target.files)} />
      {!photos.length && (
        <button type="button" onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-[#1E2F45] hover:border-[#FFD500]/60 rounded-sm py-16 flex flex-col items-center gap-3 text-slate-400 transition-colors" data-testid="photos-empty">
          <ImagePlus size={36} className="text-[#FFD500]" />
          <span className="font-display font-bold uppercase tracking-wider text-lg text-slate-200">Nenhuma foto registrada</span>
          <span className="text-sm">Clique para selecionar imagens (JPG/PNG). As imagens são otimizadas automaticamente.</span>
        </button>
      )}
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {photos.map((p, i) => (
          <PhotoCard key={p.id} photo={p} index={i} anomalies={insp.anomalies || []} onChange={(patch) => patchLocal(p.id, patch)} onPersist={(patch) => persist(p.id, patch)}
            onDelete={() => remove(p.id)} onAnnotate={() => setAnnotating(p)} />
        ))}
      </div>
      {annotating && (
        <PhotoAnnotator photo={annotating} onClose={() => setAnnotating(null)}
          onSave={async (shapes) => { patchLocal(annotating.id, { annotations: shapes }); await persist(annotating.id, { annotations: shapes }); toast.success("Anotações salvas"); setAnnotating(null); }} />
      )}
    </div>
  );
}
